import React, { useEffect, useMemo, useRef, useState } from "react";

import { Tooltip } from "@mui/material";
import ShipeeziNewGrid, {
  RowActionsExtra,
  Constants as GridConstants,
} from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { FiEdit } from "react-icons/fi";
import { Contact } from "Models/contact";
import { Timezone } from "Models/timezone";
import BadgeStatus from "Components/Generic/BadgeStatus";
import { Status } from "Util/enums";
import { Filters, Searches } from "Models/shipeezi.table";

type Props = {
  contacts: Contact[];
  onChange?: (contacts: Contact[]) => void;
  onClickEditContact?: (contact?: Contact) => void;
  timezones?: Timezone[];
  showHeaderGrid?: boolean;
  viewOnly?: boolean;
  showFullTable?: boolean;
};

export const TableListContacts = (props: Props) => {
  const { contacts, onChange, showHeaderGrid, viewOnly, showFullTable, onClickEditContact } = props;
  
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(showFullTable ? 20 : 5);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();

  const { t: translate } = useTranslation();

  const handlePageSize = (pageSize: number) => {
    setPageSize(pageSize);
  };

  const handleCurrentPage = (numPage: number) => {
    setPage(numPage - 1);
  };

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const activeOrInactiveContact = (id: string | number, contact: Contact) => {
    if (contact) {
      const contactUpdate: Contact = {
        ...contact,
        status: contact.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
      };

      const contactsUpdated = [...contacts];

      const index = contacts.findIndex((c) => c === contact);
      if (index !== -1) {
        contactsUpdated[index] = contactUpdate;
        onChange?.(contactsUpdated);
      }
    }
  };

  const rowActionsExtra: RowActionsExtra[] = [
    {
      title: translate("edit"),
      key: "edit",
      buttonAndAction: (id: string | number, row) => {
        const index = contacts.findIndex((c) => c === row);
        return (
          <Tooltip title={translate("edit")} placement="top">
            <div>
              <FiEdit
                size={GridConstants.defaultRowActionSize}
                color={GridConstants.defaultColorIcon}
                onClick={() => onClickEditContact?.(contacts[index]!)}
                data-cy="action-edit"
              />
            </div>
          </Tooltip>
        );
      },
    },
  ];

  const filteredContacts = useMemo(() => {
    let result = contacts;

    // Aplicar busca (searches)
    if (searches?.length) {
      result = result.filter((contact) =>
        searches.every((search) =>
          Object.entries(search).every(([key, value]) => {
            const contactValue = (contact as any)[key];

            if (typeof contactValue === "object" && contactValue !== null) {
              const stringValue =
                contactValue.nameFormatted || contactValue.name || JSON.stringify(contactValue);

              return stringValue?.toLowerCase().includes(value.toLowerCase());
            }

            return contactValue?.toString().toLowerCase().includes(value.toLowerCase());
          })
        )
      );
    }

    // Aplicar filtros (filters)
    if (filters && Object.keys(filters).length > 0) {
      result = result.filter((contact) =>
        Object.entries(filters).every(([filterKey, filterValues]) => {
          if (!filterValues || filterValues.length === 0) {
            return true;
          }

          const contactValue = (contact as any)[filterKey];
          
          if (typeof contactValue === "object" && contactValue !== null) {
            const stringValue = contactValue.nameFormatted || contactValue.name || contactValue.toString();
            return filterValues.some(filterValue => 
              stringValue?.toLowerCase() === filterValue.toLowerCase()
            );
          }

          return filterValues.some(filterValue => 
            contactValue?.toString().toLowerCase() === filterValue.toLowerCase()
          );
        })
      );
    }

    return result;
  }, [contacts, searches, filters]);

  const paginatedContacts = useMemo(() => {
    return filteredContacts?.slice(page * pageSize, page * pageSize + pageSize);
  }, [filteredContacts, page, pageSize]);

  return (
    <ShipeeziNewGrid
      data={paginatedContacts}
      columns={[
        {
          name: "firstName",
          title: translate("name"),
        },
        {
          name: "email",
          title: translate("email"),
        },
        {
          name: "mobilePhone",
          title: translate("mobileNumber"),
        },
        {
          name: "phone",
          title: translate("phone"),
        },
        {
          name: "timezone",
          title: translate("timezone"),
          formatComponent(value: Timezone) {
            return value?.nameFormatted;
          },
          hideSort: true,
        },
        {
          name: "contactType",
          title: translate("contactType"),
        },

        {
          name: "status",
          title: translate("status"),
          formatComponent: (value?: string) => <BadgeStatus value={value} />,
          hideSearch: true,
        },
      ]}
      gridCellPadding="16px"
      gridHeaderCellPadding="7px 16px"
      namePage={translate("items2")}
      pageSize={pageSize}
      pageSizeList={showFullTable ? [20, 40, 60, 100] : undefined}
      onChangePageSize={handlePageSize}
      totalItems={filteredContacts?.length} // Usar filteredContacts ao invés de contacts
      emptyData={translate("noInformationToBeShown")}
      currentPage={page}
      onNextPage={handleCurrentPage}
      onPreviousPage={handleCurrentPage}
      onGoToPage={handleCurrentPage}
      showFooterPageInfo={!!showFullTable}
      showFooterPaginationInfo={!!showFullTable}
      alwaysShowFooter
      footerStyle={showFullTable ? { marginTop: "auto" } : undefined}
      rowActionsExtra={!viewOnly ? rowActionsExtra : undefined}
      style={showFullTable ? { flex: 1, display: "flex", flexDirection: "column" } : undefined}
      rowActions={
        !viewOnly
          ? {
              orderBy: ["extraActions", "handleDetails", "handleChange"],
              handleChange: (rowId, row: Contact) => activeOrInactiveContact(rowId, row),
            }
          : undefined
      }
      generalActions={{
        orderBy: {
          left: ["handleSearch", "handleFilterBy"],
        },
        handleSearch: {
          onSearch: (filters) => handleSearch(filters),
        },
        handleFilterBy: {
          categories: [
            {
              name: "Status",
              options: [Status.ACTIVE, Status.INACTIVE],
            },
          ],
          onChange: handleFilter,
        },
      }}
      showSelection={false}
      positionActionsColumn={"right"}
      showHeaderGrid={showHeaderGrid}
      showFooterGrid={true}
      showColumnChooser={false}
    />
  );
};